import { FC, useContext, useEffect, useRef } from 'react';
import Icon from '@common/assets';
import { JobPostModalContext } from '@components/common/jobPostModal/JobPostModalWrap';
import { useHandleCalendarData } from '@components/jobs/bigCalendar/hook';
import { BigCalendarContext, CalendarContext } from '@components/jobs/contextWrap';
import PopUpItem from './PopUpItem';
import { LatestOpenType } from '../../index';
import { CalendarPcListVO, JobPostingBookmarkType } from '@domain/vo/calendar/CalendarPcListVO';
import useClickAway from '@utils/hooks/useClickOutside';

import classnames from 'classnames/bind';
import styles from './jobPostPopUp.module.scss';
const cx = classnames.bind(styles);

interface JobPostPopUpProps {
  calendarData: CalendarPcListVO;
  setLatestOpen: (type: LatestOpenType) => void;
  openLoginModal: () => void;
  jobPostingBookmarkMap?: JobPostingBookmarkType;
  updateBookmarkStatus: (jobPostingSn: number, newBookmarkStatus: boolean) => void;
}

const JobPostPopUp: FC<JobPostPopUpProps> = ({
  calendarData,
  openLoginModal,
  setLatestOpen,
  jobPostingBookmarkMap,
  updateBookmarkStatus,
}) => {
  const { onOpenJobPostModal } = useContext(JobPostModalContext);
  const { calendarInfo, filter } = useContext(CalendarContext);
  const { filterCondition, jobPostPopup } = useContext(BigCalendarContext);

  const ref = useRef<HTMLDivElement>(null);

  const { getAllJobPostingList } = useHandleCalendarData({
    calendarDate: calendarInfo.calendarDate,
    aiScreeningYn: filterCondition.aiScreeningYn,
    bookmarkYn: filterCondition.bookmarkYn,
    jobPostStatus: filterCondition.jobPostStatus,
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        handleCloseJobPost();
        return;
    }
  };

  //공고 팝업 요소 클릭 이벤트
  const handleJopPostPopUpClick = (key: string, sn: number) => {
    onOpenJobPostModal(key, sn, getAllJobPostingList(calendarData.calendarItems));
    jobPostPopup.setJobPostPopUpData(null);
  };

  const handleCloseJobPost = () => {
    if (filter.visibleFilter) setLatestOpen('FILTER');
    else setLatestOpen(null);
    jobPostPopup.setJobPostPopUpData(null);
  };

  useClickAway(handleCloseJobPost, ref);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // 공고가 4개 이상인 경우에만 스크롤 되도록 처리
  const isScroll =
    !!jobPostPopup.jobPostPopUpData?.jobPostings && jobPostPopup.jobPostPopUpData?.jobPostings.length > 3;

  const jobPostEl = jobPostPopup.jobPostPopUpData?.jobPostings.map((jobPosting) => {
    return (
      <PopUpItem
        key={jobPosting.sn}
        jobPostingData={jobPosting}
        jobPostingBookmarkMap={jobPostingBookmarkMap}
        onClickItem={() =>
          handleJopPostPopUpClick(`${jobPosting.sn}-${jobPostPopup.jobPostPopUpData?.statusType}`, jobPosting.sn)
        }
        openLoginModal={openLoginModal}
        handleBookmark={updateBookmarkStatus}
      />
    );
  });

  return (
    <div
      className={cx('jobPostPopUp')}
      ref={ref}
      style={{ top: jobPostPopup.jobPostingPopUpPosition?.y, left: jobPostPopup.jobPostingPopUpPosition?.x }}
    >
      <div className={cx('titleArea')}>
        <div className={cx('title')}>{jobPostPopup.jobPostPopUpData?.companyName} 공고</div>
        <a className={cx('btnClose')} role="button" onClick={handleCloseJobPost}>
          <Icon name="closeLight" width={24} height={24} />
        </a>
      </div>

      <div className={cx('jobPostArea')}>
        <div className={cx('jobPostInner', { scroll: isScroll })}>{jobPostEl}</div>
      </div>
    </div>
  );
};

export default JobPostPopUp;
